import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useAppConfig } from '@/hooks/useShared';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Button, Card } from '@/components/ui';
import { purgeSoftDeletedGames } from '@/services/system.service';
import { Location, TelegramChatConfig } from '@/lib/types';
import { canManageUsers } from '@/lib/roles';
import { ChipColorConfig } from '@/components/chip-scanner/ChipColorConfig';

const APP_VERSION = '0.1.001';

export function SettingsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { config } = useAppConfig();
  const telegramRef = useRef<HTMLDivElement>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [purging, setPurging] = useState(false);
  const [purgeResult, setPurgeResult] = useState<string | null>(null);

  const [gameSettings, setGameSettings] = useState({
    default_buy_in: config?.default_buy_in ?? 20,
    default_game_title: config?.default_game_title || 'Poker Night',
    default_game_day: config?.default_game_day ?? 5,
    default_game_time: config?.default_game_time || '20:00',
    min_players: config?.min_players ?? 2,
    amount_increment: config?.amount_increment ?? 0.50,
    currency_symbol: config?.currency_symbol || '$',
  });

  const [venues, setVenues] = useState<Location[]>(config?.location_list || []);
  const [showVenueModal, setShowVenueModal] = useState(false);
  const [editingVenue, setEditingVenue] = useState<Location | null>(null);
  const [venueForm, setVenueForm] = useState({ label: '', address: '', notes: '' });

  const [chatConfigs, setChatConfigs] = useState<TelegramChatConfig[]>([]);
  const [activeChatId, setActiveChatId] = useState('');
  const [showChatModal, setShowChatModal] = useState(false);
  const [editingChat, setEditingChat] = useState<TelegramChatConfig | null>(null);
  const [chatForm, setChatForm] = useState({ label: '', chat_id: '', description: '' });

  const DAYS_OF_WEEK = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
  ];

  const TIME_OPTIONS = [
    '18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00',
  ];

  useEffect(() => {
    if (config) {
      setGameSettings({
        default_buy_in: config.default_buy_in ?? 20,
        default_game_title: config.default_game_title || 'Poker Night',
        default_game_day: config.default_game_day ?? 5,
        default_game_time: config.default_game_time || '20:00',
        min_players: config.min_players ?? 2,
        amount_increment: config.amount_increment ?? 0.50,
        currency_symbol: config.currency_symbol || '$',
      });
    }
  }, [config]);

  useEffect(() => {
    if (config?.location_list) {
      setVenues(config.location_list);
    }
  }, [config]);

  useEffect(() => {
    if (config) {
      const configs = config.telegram_chat_configs;
      if (!configs || configs.length === 0) {
        const isEmulator = import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true';
        const defaultConfigs = [
          { id: 'test-group', label: 'Test Group', chat_id: '-5011313718', description: 'Testing environment' },
          { id: 'prod-group', label: 'Production', chat_id: '-1003761576044', description: 'Poker Knights Production' },
        ];
        setChatConfigs(defaultConfigs);
        setActiveChatId(isEmulator ? 'test-group' : 'prod-group');
      } else {
        setChatConfigs(configs);
        setActiveChatId(config.telegram_active_chat_id ?? '');
      }
    }
  }, [config]);

  // Scroll to Telegram section when navigated via ?section=telegram
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('section') === 'telegram' && telegramRef.current) {
      telegramRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.search, chatConfigs]);

  const handleSaveGameSettings = async () => {
    if (gameSettings.min_players < 2) {
      alert('Min players must be at least 2');
      return;
    }
    if (gameSettings.amount_increment <= 0) {
      alert('Amount increment must be greater than 0');
      return;
    }
    if (!gameSettings.currency_symbol.trim()) {
      alert('Currency symbol is required');
      return;
    }

    setSaving(true);
    try {
      await setDoc(doc(db, 'config', 'app'), {
        default_buy_in: gameSettings.default_buy_in,
        default_game_title: gameSettings.default_game_title,
        default_game_day: gameSettings.default_game_day,
        default_game_time: gameSettings.default_game_time,
        min_players: gameSettings.min_players,
        amount_increment: gameSettings.amount_increment,
        currency_symbol: gameSettings.currency_symbol,
      }, { merge: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefaultVenue = async (venueId: string) => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'config', 'app'), {
        default_location_id: venueId,
      }, { merge: true });
    } finally {
      setSaving(false);
    }
  };

  const handleAddVenue = () => {
    setEditingVenue(null);
    setVenueForm({ label: '', address: '', notes: '' });
    setShowVenueModal(true);
  };

  const handleEditVenue = (venue: Location) => {
    setEditingVenue(venue);
    setVenueForm({ label: venue.label, address: venue.address || '', notes: venue.notes || '' });
    setShowVenueModal(true);
  };

  const handleDeleteVenue = async (venueId: string) => {
    if (!confirm('Delete this venue?')) return;
    setSaving(true);
    try {
      const updatedVenues = venues.filter(v => v.id !== venueId);
      await setDoc(doc(db, 'config', 'app'), {
        location_list: updatedVenues,
      }, { merge: true });
      setVenues(updatedVenues);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveVenue = async () => {
    if (!venueForm.label.trim()) return;
    setSaving(true);
    try {
      let updatedVenues: Location[];
      if (editingVenue) {
        updatedVenues = venues.map(v => v.id === editingVenue.id
          ? { ...v, label: venueForm.label, address: venueForm.address, notes: venueForm.notes }
          : v
        );
      } else {
        const newVenue: Location = {
          id: crypto.randomUUID(),
          label: venueForm.label,
          address: venueForm.address,
          notes: venueForm.notes,
        };
        updatedVenues = [...venues, newVenue];
      }
      await setDoc(doc(db, 'config', 'app'), {
        location_list: updatedVenues,
      }, { merge: true });
      setVenues(updatedVenues);
      setShowVenueModal(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      console.error('Error saving venue:', error);
      alert(`Failed to save venue: ${message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleAddChat = () => {
    setEditingChat(null);
    setChatForm({ label: '', chat_id: '', description: '' });
    setShowChatModal(true);
  };

  const handleEditChat = (chat: TelegramChatConfig) => {
    setEditingChat(chat);
    setChatForm({ label: chat.label, chat_id: chat.chat_id, description: chat.description || '' });
    setShowChatModal(true);
  };

  const handleDeleteChat = (chatId: string) => {
    if (!confirm('Delete this chat configuration?')) return;
    const updated = chatConfigs.filter(c => c.id !== chatId);
    setChatConfigs(updated);
    if (activeChatId === chatId && updated.length > 0) {
      setActiveChatId(updated[0].id);
    }
  };

  const handleSetActiveChat = (chatId: string) => {
    setActiveChatId(chatId);
  };

  const handleSaveChat = () => {
    if (!chatForm.label.trim() || !chatForm.chat_id.trim()) return;
    if (!/^-?\d+$/.test(chatForm.chat_id.trim())) {
      alert('Chat ID must be a valid number');
      return;
    }

    let updated: TelegramChatConfig[];
    if (editingChat) {
      updated = chatConfigs.map(c =>
        c.id === editingChat.id
          ? { ...c, label: chatForm.label.trim(), chat_id: chatForm.chat_id.trim(), description: chatForm.description.trim() || undefined }
          : c
      );
    } else {
      const newChat: TelegramChatConfig = {
        id: crypto.randomUUID(),
        label: chatForm.label.trim(),
        chat_id: chatForm.chat_id.trim(),
        description: chatForm.description.trim() || undefined,
      };
      updated = [...chatConfigs, newChat];
    }
    setChatConfigs(updated);
    if (updated.length === 1) {
      setActiveChatId(updated[0].id);
    }
    setShowChatModal(false);
  };

  const handleSaveTelegramSettings = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'config', 'app'), {
        telegram_chat_configs: chatConfigs,
        telegram_active_chat_id: activeChatId,
      }, { merge: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  const handlePurgeDeletedGames = async () => {
    if (!user) return;
    if (!window.confirm(
      'This will permanently delete ALL soft-deleted games and their settlement records. This cannot be undone. Continue?'
    )) return;

    setPurging(true);
    setPurgeResult(null);
    try {
      const result = await purgeSoftDeletedGames(user.id);
      if (result.errors.length > 0) {
        setPurgeResult(`Purged ${result.gamesPurged} games with ${result.errors.length} errors.`);
        console.error('Purge errors:', result.errors);
      } else if (result.gamesPurged === 0) {
        setPurgeResult('No soft-deleted games found.');
      } else {
        setPurgeResult(`Purged ${result.gamesPurged} games successfully.`);
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setPurgeResult(`Error: ${msg}`);
    } finally {
      setPurging(false);
      setTimeout(() => setPurgeResult(null), 5000);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth', { replace: true });
  };

  if (!user) return null;

  return (
    <div className="max-w-md mx-auto py-6 px-4 space-y-6 min-h-screen bg-[#f5f7f8]">
      <div className="bg-[#e8f5f0] border border-[#006855]/10 rounded-2xl p-5 mb-4">
        <div className="text-[10px] font-black uppercase tracking-[2px] text-[#006855]/70 mb-1">APP CONFIGURATION</div>
        <h1 className="text-[22px] font-extrabold text-[#2c2f30] leading-tight">Settings</h1>
      </div>

      {saved && (
        <div className="bg-[#dcfce7] text-[#166534] p-3 rounded-lg text-center text-sm font-medium">
          Settings saved!
        </div>
      )}

      <Card className="border border-gray-200">
        <h2 className="text-sm font-semibold text-[#2c2f30] mb-4">Game Settings</h2>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-[#6b7280] mb-1">Default Buy-in ($)</label>
            <input
              type="number"
              value={gameSettings.default_buy_in}
              onChange={e => setGameSettings(s => ({ ...s, default_buy_in: Number(e.target.value) }))}
              className="w-full rounded-lg border border-gray-200 bg-white p-3 text-sm font-medium text-[#2c2f30] focus:outline-none focus:ring-2 focus:ring-[#006855]/20 focus:border-[#006855]"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-[#6b7280] mb-1">Default Game Title</label>
            <input
              type="text"
              value={gameSettings.default_game_title}
              onChange={e => setGameSettings(s => ({ ...s, default_game_title: e.target.value }))}
              placeholder="e.g., Poker Night"
              className="w-full rounded-lg border border-gray-200 bg-white p-3 text-sm font-medium text-[#2c2f30] focus:outline-none focus:ring-2 focus:ring-[#006855]/20 focus:border-[#006855]"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-[#6b7280] mb-1">Default Day</label>
            <select
              value={gameSettings.default_game_day}
              onChange={e => setGameSettings(s => ({ ...s, default_game_day: Number(e.target.value) }))}
              className="w-full rounded-lg border border-gray-200 bg-white p-3 text-sm font-medium text-[#2c2f30] focus:outline-none focus:ring-2 focus:ring-[#006855]/20 focus:border-[#006855]"
            >
              {DAYS_OF_WEEK.map(d => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#6b7280] mb-1">Default Time</label>
            <select
              value={gameSettings.default_game_time}
              onChange={e => setGameSettings(s => ({ ...s, default_game_time: e.target.value }))}
              className="w-full rounded-lg border border-gray-200 bg-white p-3 text-sm font-medium text-[#2c2f30] focus:outline-none focus:ring-2 focus:ring-[#006855]/20 focus:border-[#006855]"
            >
              {TIME_OPTIONS.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#6b7280] mb-1">Min Players</label>
            <input
              type="number"
              value={gameSettings.min_players}
              onChange={e => setGameSettings(s => ({ ...s, min_players: Number(e.target.value) }))}
              className="w-full rounded-lg border border-gray-200 bg-white p-3 text-sm font-medium text-[#2c2f30] focus:outline-none focus:ring-2 focus:ring-[#006855]/20 focus:border-[#006855]"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-[#6b7280] mb-1">Amount Increment</label>
            <input
              type="number"
              step="0.01"
              value={gameSettings.amount_increment}
              onChange={e => setGameSettings(s => ({ ...s, amount_increment: Number(e.target.value) }))}
              className="w-full rounded-lg border border-gray-200 bg-white p-3 text-sm font-medium text-[#2c2f30] focus:outline-none focus:ring-2 focus:ring-[#006855]/20 focus:border-[#006855]"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-[#6b7280] mb-1">Currency Symbol</label>
            <input
              type="text"
              value={gameSettings.currency_symbol}
              onChange={e => setGameSettings(s => ({ ...s, currency_symbol: e.target.value }))}
              className="w-full rounded-lg border border-gray-200 bg-white p-3 text-sm font-medium text-[#2c2f30] focus:outline-none focus:ring-2 focus:ring-[#006855]/20 focus:border-[#006855]"
            />
          </div>

          <Button fullWidth onClick={handleSaveGameSettings} disabled={saving}>
            {saving ? 'Saving...' : 'Save Game Settings'}
          </Button>
        </div>
      </Card>

      <Card className="border border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-sm font-semibold text-[#2c2f30]">Venues</h2>
          <button
            onClick={handleAddVenue}
            className="text-xs font-semibold text-[#006855] hover:text-[#006855]/80 transition-colors"
          >
            + Add
          </button>
        </div>

        {venues.length === 0 ? (
          <div className="text-[#6b7280] text-sm text-center py-4">
            No venues yet. Add one to get started.
          </div>
        ) : (
          <div className="space-y-0">
            {venues.map((venue, index) => (
              <div
                key={venue.id}
                className={`flex items-center py-3 ${index !== venues.length - 1 ? 'border-b border-gray-100' : ''}`}
              >
                <input
                  type="radio"
                  name="defaultVenue"
                  checked={config?.default_location_id === venue.id}
                  onChange={() => handleSetDefaultVenue(venue.id)}
                  className="mr-3"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#2c2f30] truncate">{venue.label}</div>
                  {venue.address && <div className="text-xs text-[#6b7280] truncate">{venue.address}</div>}
                </div>
                <button
                  onClick={() => handleEditVenue(venue)}
                  className="text-xs text-[#006855] hover:text-[#006855]/80 transition-colors mr-3 ml-2"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDeleteVenue(venue.id)}
                  className="text-xs text-red-500 hover:text-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {canManageUsers(user) && <ChipColorConfig />}

      <Card className="border border-gray-200">
        <h2 className="text-sm font-semibold text-[#2c2f30] mb-4">Session</h2>
        <Button variant="danger" fullWidth onClick={() => {
          void handleSignOut();
        }}>
          Sign Out
        </Button>
      </Card>

      {canManageUsers(user) && (
        <Card className="border border-gray-200">
          <h2 className="text-sm font-semibold text-[#2c2f30] mb-4">Admin Users</h2>
          <Button variant="secondary" fullWidth onClick={() => navigate('/admin/users')}>
            Open Admin Users
          </Button>
        </Card>
      )}

      {canManageUsers(user) && (
        <div ref={telegramRef} id="telegram-chats" className="scroll-mt-24">
        <Card className="border border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-sm font-semibold text-[#2c2f30]">Telegram Chats</h2>
            <button
              onClick={handleAddChat}
              className="text-xs font-semibold text-[#006855] hover:text-[#006855]/80 transition-colors"
            >
              + Add
            </button>
          </div>

          {chatConfigs.length === 0 ? (
            <div className="text-[#6b7280] text-sm text-center py-4">
              No chat configurations yet. Add one to get started.
            </div>
          ) : (
            <div className="space-y-0 mb-4">
                <div className="flex items-start py-3 border-b border-gray-100">
                  <input
                    type="radio"
                    name="activeChat"
                    checked={activeChatId === ''}
                    onChange={() => handleSetActiveChat('')}
                    className="mr-3 mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#2c2f30]">None</div>
                    <div className="text-xs text-[#6b7280]">Disable all Telegram notifications</div>
                  </div>
                </div>
              {chatConfigs.map((chat, index) => (
                <div
                  key={chat.id}
                  className={`flex items-start py-3 ${index !== chatConfigs.length - 1 ? 'border-b border-gray-100' : ''}`}
                >
                  <input
                    type="radio"
                    name="activeChat"
                    checked={activeChatId === chat.id}
                    onChange={() => handleSetActiveChat(chat.id)}
                    className="mr-3 mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#2c2f30]">{chat.label}</div>
                    <div className="text-xs text-[#6b7280]">{chat.chat_id}</div>
                    {chat.description && (
                      <div className="text-xs text-[#9ca3af] mt-0.5">{chat.description}</div>
                    )}
                  </div>
                  <button
                    onClick={() => handleEditChat(chat)}
                    className="text-xs text-[#006855] hover:text-[#006855]/80 transition-colors mr-3 ml-2"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteChat(chat.id)}
                    className="text-xs text-red-500 hover:text-red-700 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}

          {chatConfigs.length > 0 && (
            <Button fullWidth onClick={handleSaveTelegramSettings} disabled={saving}>
              {saving ? 'Saving...' : 'Save Telegram Settings'}
            </Button>
          )}
        </Card>
        </div>
      )}

      {canManageUsers(user) && (
        <Card className="border-red-200">
          <h2 className="text-[10px] font-black text-red-600 uppercase tracking-[2px] mb-4">Data Management</h2>
          <p className="text-xs text-[#6b7280] mb-3">
            Permanently delete all soft-deleted games and their associated settlement records.
          </p>
          <Button variant="danger" fullWidth onClick={handlePurgeDeletedGames} disabled={purging}>
            {purging ? 'Purging...' : 'Purge Soft-Deleted Games'}
          </Button>
          {purgeResult && (
            <div className="mt-2 text-xs text-[#6b7280] text-center">{purgeResult}</div>
          )}
        </Card>
      )}

      <div className="text-center py-4 text-[#6b7280] text-xs">
        Poker Knights v{APP_VERSION}
      </div>

      {showVenueModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[1000]" onClick={() => setShowVenueModal(false)}>
          <div className="bg-white rounded-xl p-6 w-[90%] max-w-[400px]" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-[#2c2f30] mb-5">
              {editingVenue ? 'Edit Venue' : 'Add Venue'}
            </h3>

            <div className="mb-4">
              <label className="block text-xs font-medium text-[#6b7280] mb-1">Label *</label>
              <input
                type="text"
                value={venueForm.label}
                onChange={e => setVenueForm(f => ({ ...f, label: e.target.value }))}
                placeholder="e.g., Joe's House"
                className="w-full rounded-lg border border-gray-200 bg-white p-3 text-sm font-medium text-[#2c2f30] focus:outline-none focus:ring-2 focus:ring-[#006855]/20 focus:border-[#006855]"
              />
            </div>

            <div className="mb-4">
              <label className="block text-xs font-medium text-[#6b7280] mb-1">Address</label>
              <input
                type="text"
                value={venueForm.address}
                onChange={e => setVenueForm(f => ({ ...f, address: e.target.value }))}
                placeholder="123 Main St, City"
                className="w-full rounded-lg border border-gray-200 bg-white p-3 text-sm font-medium text-[#2c2f30] focus:outline-none focus:ring-2 focus:ring-[#006855]/20 focus:border-[#006855]"
              />
            </div>

            <div className="mb-5">
              <label className="block text-xs font-medium text-[#6b7280] mb-1">Notes</label>
              <textarea
                value={venueForm.notes}
                onChange={e => setVenueForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Optional notes..."
                rows={3}
                className="w-full rounded-lg border border-gray-200 bg-white p-3 text-sm font-medium text-[#2c2f30] focus:outline-none focus:ring-2 focus:ring-[#006855]/20 focus:border-[#006855] resize-y"
              />
            </div>

            <div className="flex gap-3">
              <Button variant="ghost" className="flex-1" onClick={() => setShowVenueModal(false)}>
                Cancel
              </Button>
              <Button className="flex-1" onClick={handleSaveVenue} disabled={saving || !venueForm.label.trim()}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showChatModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[1000]" onClick={() => setShowChatModal(false)}>
          <div className="bg-white rounded-xl p-6 w-[90%] max-w-[400px]" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-[#2c2f30] mb-5">
              {editingChat ? 'Edit Chat' : 'Add Chat'}
            </h3>

            <div className="mb-4">
              <label className="block text-xs font-medium text-[#6b7280] mb-1">Label *</label>
              <input
                type="text"
                value={chatForm.label}
                onChange={e => setChatForm(f => ({ ...f, label: e.target.value }))}
                placeholder="e.g., Test Group"
                className="w-full rounded-lg border border-gray-200 bg-white p-3 text-sm font-medium text-[#2c2f30] focus:outline-none focus:ring-2 focus:ring-[#006855]/20 focus:border-[#006855]"
              />
            </div>

            <div className="mb-4">
              <label className="block text-xs font-medium text-[#6b7280] mb-1">Chat ID *</label>
              <input
                type="text"
                value={chatForm.chat_id}
                onChange={e => setChatForm(f => ({ ...f, chat_id: e.target.value }))}
                placeholder="e.g., -5011313718"
                className="w-full rounded-lg border border-gray-200 bg-white p-3 text-sm font-medium text-[#2c2f30] focus:outline-none focus:ring-2 focus:ring-[#006855]/20 focus:border-[#006855]"
              />
            </div>

            <div className="mb-5">
              <label className="block text-xs font-medium text-[#6b7280] mb-1">Description</label>
              <textarea
                value={chatForm.description}
                onChange={e => setChatForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Optional notes..."
                rows={2}
                className="w-full rounded-lg border border-gray-200 bg-white p-3 text-sm font-medium text-[#2c2f30] focus:outline-none focus:ring-2 focus:ring-[#006855]/20 focus:border-[#006855] resize-y"
              />
            </div>

            <div className="flex gap-3">
              <Button variant="ghost" className="flex-1" onClick={() => setShowChatModal(false)}>
                Cancel
              </Button>
              <Button className="flex-1" onClick={handleSaveChat} disabled={!chatForm.label.trim() || !chatForm.chat_id.trim()}>
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
